import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, apiError, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReportDialog } from "@/components/ReportDialog";
import { PremiumBadge } from "@/components/PremiumBadge";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useContactExchange, ExchangeContactsButton, ContactCardReveal } from "@/components/ContactExchange";
import { MeetupIdeasButton, MeetupCard } from "@/components/MeetupSpots";
import { AgreedSpotBanner, DateCheckinCard } from "@/components/ChatExtras";
import { SecondDateCard } from "@/components/SecondDate";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, Flag, Trash2, Pencil, Check, Lock, Video, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "recently";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Tracks the visible viewport so the input bar stays above the iOS/Android software keyboard
function useVisualViewportHeight() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => { setH(vv.height); window.scrollTo(0, 0); };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);
  return h;
}

export default function Chat() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [seen, setSeen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [wsLive, setWsLive] = useState(false);
  const vh = useVisualViewportHeight();

  useEffect(() => {
    api.post("/presence/ping").catch(() => {});
    const iv = setInterval(() => api.post("/presence/ping").catch(() => {}), 20000);
    return () => clearInterval(iv);
  }, []);
  const scrollRef = useRef(null);
  const lastTypingRef = useRef(0);
  const typingTimerRef = useRef(null);

  const { data: match } = useQuery({ queryKey: ["match", matchId], queryFn: async () => (await api.get(`/matches/${matchId}`)).data });
  const [initial, setInitial] = useState([]);
  const [live, setLive] = useState([]);
  const wsRef = useRef(null);
  const confirmed = match && match.status === "confirmed";
  const [contacts, setContacts, loadContacts] = useContactExchange(matchId, confirmed);

  const messages = useMemo(() => {
    const map = new Map();
    [...live, ...initial].forEach((m) => map.set(m.id, m));
    return [...map.values()].sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [initial, live]);

  const MSG_LIMIT = user.premium ? 20 : 10;
  const since = match?.bonus_call_started_at || "";
  const myCount = messages.filter((m) => m.sender_id === user.user_id && m.kind !== "meetup" && m.kind !== "second_date" && m.created_at > since).length;
  const remaining = Math.max(0, MSG_LIMIT - myCount);
  const locked = remaining === 0;

  // initial history
  useEffect(() => {
    if (!confirmed) return;
    let active = true;
    api.get(`/matches/${matchId}/messages`).then(({ data }) => {
      if (!active) return;
      setInitial(data);
      const mine = data.filter((m) => m.sender_id === user.user_id);
      if (mine.length && mine[mine.length - 1].read) setSeen(true);
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, confirmed]);

  // realtime websocket (token auth, auto-reconnect) + polling fallback
  useEffect(() => {
    if (!confirmed) return;
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    let ws; let closed = false; let retry;
    const onPayload = (payload) => {
      if (payload.type === "message") {
        setPartnerTyping(false);
        setLive((prev) => [...prev, payload.message]);
        if (payload.message.sender_id !== user.user_id && ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "read" }));
      } else if (payload.type === "typing" && payload.user_id !== user.user_id) {
        setPartnerTyping(true);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setPartnerTyping(false), 3000);
      } else if (payload.type === "read" && payload.reader_id !== user.user_id) {
        setSeen(true);
        setLive((p) => p.map((m) => (m.sender_id === user.user_id ? { ...m, read: true } : m)));
        setInitial((p) => p.map((m) => (m.sender_id === user.user_id ? { ...m, read: true } : m)));
      } else if (payload.type === "delete") {
        setLive((p) => p.filter((m) => m.id !== payload.id));
        setInitial((p) => p.filter((m) => m.id !== payload.id));
      } else if (payload.type === "contact_exchange") {
        loadContacts();
      } else if (payload.type === "error") {
        toast.error(payload.detail?.message || "Message not sent");
      } else if (payload.type === "meetup_update") {
        updateMeetup(payload.id, payload.meetup);
      } else if (payload.type === "second_date_update") {
        updateMeetup(payload.id, payload.second_date, "second_date");
      } else if (payload.type === "edit") {
        setLive((p) => p.map((m) => (m.id === payload.id ? { ...m, text: payload.text, edited: true } : m)));
        setInitial((p) => p.map((m) => (m.id === payload.id ? { ...m, text: payload.text, edited: true } : m)));
      }
    };
    const connect = async () => {
      if (closed) return;
      let token = "";
      try { token = (await api.get("/ws-token")).data.token; } catch {}
      if (closed) return;
      ws = new WebSocket(`${base}/api/ws/chat/${matchId}${token ? `?token=${encodeURIComponent(token)}` : ""}`);
      wsRef.current = ws;
      ws.onopen = () => { setWsLive(true); try { ws.send(JSON.stringify({ type: "read" })); } catch {} };
      ws.onmessage = (e) => { try { onPayload(JSON.parse(e.data)); } catch {} };
      ws.onclose = () => { setWsLive(false); if (!closed) retry = setTimeout(connect, 3000); };
    };
    connect();
    const poll = setInterval(() => {
      api.get(`/matches/${matchId}/messages`).then(({ data }) => setInitial(data)).catch(() => {});
    }, 4000);
    return () => { closed = true; clearInterval(poll); clearTimeout(retry); if (ws) ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, confirmed]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, partnerTyping]);

  const send = async (e) => {
    e.preventDefault();
    const val = text.trim();
    if (!val) return;
    if (editing) return saveEdit(val);
    setText(""); setSending(true); setSeen(false);
    try {
      const { data } = await api.post(`/matches/${matchId}/messages`, { text: val });
      if (data) setLive((p) => [...p, data]);
    } catch (err) {
      setText(val);
      if (err.response?.status !== 402) toast.error(apiError(err.response?.data?.detail) || "Message not sent — check your connection and try again");
    } finally { setSending(false); }
  };

  const startEdit = (m) => { setEditing({ id: m.id }); setText(m.text); };
  const updateMeetup = (id, meetup, key = "meetup") => {
    const fix = (p) => p.map((m) => (m.id === id ? { ...m, [key]: meetup } : m));
    setLive(fix); setInitial(fix);
  };
  const onProposed = (msg) => {
    const key = msg.kind;
    const stale = (p) => p.map((m) => (m[key]?.status === "proposed" ? { ...m, [key]: { ...m[key], status: "replaced" } } : m));
    setInitial(stale); setLive((p) => [...stale(p), msg]);
  };
  const cancelEdit = () => { setEditing(null); setText(""); };
  const saveEdit = async (val) => {
    const id = editing.id;
    const prevMsg = messages.find((m) => m.id === id);
    setEditing(null); setText(""); setSending(true);
    setLive((p) => p.map((m) => (m.id === id ? { ...m, text: val, edited: true } : m)));
    setInitial((p) => p.map((m) => (m.id === id ? { ...m, text: val, edited: true } : m)));
    try { await api.put(`/matches/${matchId}/messages/${id}`, { text: val }); }
    catch (e) {
      if (prevMsg) {
        setLive((p) => p.map((m) => (m.id === id ? prevMsg : m)));
        setInitial((p) => p.map((m) => (m.id === id ? prevMsg : m)));
      }
      toast.error(e.response?.data?.detail || "Couldn't edit");
    }
    finally { setSending(false); }
  };

  const notifyTyping = () => {
    const ws = wsRef.current;
    const now = Date.now();
    if (ws && ws.readyState === WebSocket.OPEN && now - lastTypingRef.current > 1500) {
      lastTypingRef.current = now;
      ws.send(JSON.stringify({ type: "typing" }));
    }
  };

  const unsend = async (id) => {
    const removed = messages.find((m) => m.id === id);
    setLive((p) => p.filter((m) => m.id !== id));
    setInitial((p) => p.filter((m) => m.id !== id));
    try {
      await api.delete(`/matches/${matchId}/messages/${id}`);
      toast.success("Message unsent");
    } catch (e) {
      if (removed) setLive((p) => [...p, removed]);
      toast.error(e.response?.data?.detail || "Couldn't unsend");
    }
  };

  if (!match) return <div className="h-[100dvh] bg-white/5 animate-pulse" />;

  return (
    <div data-testid="chat-screen" className="fixed inset-x-0 top-0 h-[100dvh] overflow-hidden flex flex-col bg-[var(--doek-bg)]" style={{ height: vh || undefined }}>
     <div className="max-w-3xl w-full mx-auto flex-1 min-h-0 flex flex-col px-4">
      <div data-testid="chat-header" className="flex-shrink-0 flex items-center gap-3 py-3 border-b border-white/5" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <button data-testid="chat-back" onClick={() => navigate("/messages")} className="text-emerald-100/70 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
        <img src={imgUrl(match.partner?.photos?.[0] || match.partner?.picture)} alt={match.partner?.name} className="h-11 w-11 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-lg truncate">{match.partner?.name}</h2>
            {match.partner?.verified && <VerifiedBadge size="xs" />}
            {match.partner?.premium && <PremiumBadge size="xs" showLabel={false} />}
          </div>
          {match.partner?.online ? (
            <p className="text-xs text-emerald-300 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Online now</p>
          ) : match.partner?.last_seen ? (
            <p className="text-xs text-emerald-100/50">Active {timeAgo(match.partner.last_seen)}</p>
          ) : (
            <p className="text-xs text-emerald-300/80">Confirmed · vibe-checked ✓</p>
          )}
          <p data-testid="chat-connection-status" className={`text-[10px] ${wsLive ? "text-emerald-400/70" : "text-amber-300/70"}`}>{wsLive ? "● Live" : "○ Syncing…"}</p>
        </div>
        <ReportDialog reportedId={match.partner?.user_id} matchId={matchId}
          trigger={<button className="text-pink-300/80 hover:text-pink-200"><Flag className="h-5 w-5" /></button>}
          onBlocked={() => navigate("/messages")} />
      </div>

      <div data-testid="message-counter" className="flex-shrink-0 py-1.5 text-center text-[11px] text-slate-300">
        {locked ? (
          <span className="inline-flex items-center gap-1.5"><Lock className="h-3 w-3 text-amber-400" /> Message limit reached — time for your video vibe check</span>
        ) : (
          <span data-testid="free-messages-banner" className="inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-amber-300" /> <b className="text-amber-300">{MSG_LIMIT} Free Messages Unlocked</b> · <b className="text-pink-400" data-testid="messages-left-count">{remaining}</b> left before your vibe check
          </span>
        )}
      </div>

      <AgreedSpotBanner spot={[...messages].reverse().find((m) => m.meetup?.status === "accepted")?.meetup.spot || match.meetup_spot} />
      <DateCheckinCard matchId={matchId} partnerName={match.partner?.name} onProposed={onProposed} />

      <div ref={scrollRef} data-testid="chat-history" className="flex-1 min-h-0 overflow-y-auto overscroll-contain no-scrollbar space-y-2 pr-1 py-2">
        {messages.length === 0 && (
          <div className="text-center text-sm text-emerald-100/50 py-10">Say hi 👋 — plan where to meet at {match.event?.venue}.</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          if (m.kind === "meetup" && m.meetup) {
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <MeetupCard m={m} mine={mine} matchId={matchId} onUpdate={updateMeetup} />
              </div>
            );
          }
          if (m.kind === "second_date" && m.second_date) {
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <SecondDateCard m={m} mine={mine} matchId={matchId} onUpdate={(id, v) => updateMeetup(id, v, "second_date")} />
              </div>
            );
          }
          return (
            <div key={m.id} className={`group flex items-center gap-2 ${mine ? "justify-end" : "justify-start"}`}>
              {mine && (
                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button data-testid={`edit-${m.id}`} onClick={() => startEdit(m)} title="Edit" className="text-emerald-100/40 hover:text-amber-300"><Pencil className="h-3.5 w-3.5" /></button>
                  {!m.read && <button data-testid={`unsend-${m.id}`} onClick={() => unsend(m.id)} title="Unsend" className="text-emerald-100/40 hover:text-pink-300"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              )}
              <div data-testid="chat-bubble" className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed ${
                mine ? "btn-gold rounded-[18px] rounded-br-md" : "glass rounded-[18px] rounded-bl-md text-emerald-50"
              }`}>
                {m.text}
                {m.edited && <span className={`block text-[10px] mt-0.5 ${mine ? "text-black/50" : "text-emerald-100/40"}`}>edited</span>}
              </div>
            </div>
          );
        })}
        {contacts?.revealed && contacts.card && <ContactCardReveal card={contacts.card} />}
        {seen && messages.length > 0 && messages[messages.length - 1].sender_id === user.user_id && (
          <div className="flex justify-end pr-1" data-testid="read-receipt">
            <span className="text-[11px] text-emerald-300/70 font-medium">Seen ✓✓</span>
          </div>
        )}
        {partnerTyping && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="glass rounded-[18px] rounded-bl-md px-4 py-3 flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-emerald-300/80 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="h-2 w-2 rounded-full bg-emerald-300/80 animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="h-2 w-2 rounded-full bg-emerald-300/80 animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
      </div>

      <div data-testid="chat-input-bar" className="flex-shrink-0 sticky bottom-0 z-50 bg-inherit pt-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
      <MeetupIdeasButton matchId={matchId} venue={match.event?.venue} onProposed={onProposed} />
      <ExchangeContactsButton matchId={matchId} state={contacts} setState={setContacts} partnerName={match.partner?.name} />
      {locked ? (
        <div data-testid="chat-locked">
          <p className="text-sm text-slate-300 text-center">You've used all {MSG_LIMIT} free messages — jump on a quick video call to lock in your plans.</p>
          <Button data-testid="launch-vibe-check" onClick={() => navigate(`/call/${matchId}?bonus=1`)} className="w-full btn-gold rounded-full h-12 mt-2 text-base">
            <Video className="h-5 w-5 mr-2" /> Start the 60-second Video Vibe Check
          </Button>
          {!user.premium && (
            <button data-testid="chat-premium-nudge" onClick={() => navigate("/premium")}
              className="w-full mt-3 text-xs text-amber-300/90 hover:text-amber-200 inline-flex items-center justify-center gap-1.5 transition-colors">
              <Crown className="h-3.5 w-3.5" /> Prefer to keep texting? Go Premium for 20 messages →
            </button>
          )}
        </div>
      ) : (
        <div>
          {messages.length > 0 && (
            <button data-testid="skip-to-vibe-check" onClick={() => navigate(`/call/${matchId}?bonus=1`)}
              className="w-full mb-2 text-xs text-amber-300/90 hover:text-amber-200 inline-flex items-center justify-center gap-1.5 transition-colors">
              <Video className="h-3.5 w-3.5" /> Both ready? Skip ahead to the 60-second Vibe Check →
            </button>
          )}
          <form onSubmit={send} className="flex items-center gap-2">
            {editing && <button type="button" data-testid="cancel-edit" onClick={cancelEdit} className="text-xs text-slate-400 hover:text-pink-300 shrink-0 px-1">Cancel</button>}
            <input data-testid="chat-input" value={text} onChange={(e) => { setText(e.target.value); notifyTyping(); }} onFocus={() => setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 250)} placeholder={editing ? "Edit your message…" : "Message…"}
              className="flex-1 h-12 rounded-full bg-white/5 border border-white/10 px-5 outline-none focus:border-amber-400/40 transition-colors" />
            <button data-testid="chat-send" type="submit" disabled={sending || !text.trim()}
              className="h-12 w-12 rounded-full btn-gold flex items-center justify-center disabled:opacity-50">{editing ? <Check className="h-5 w-5" /> : <Send className="h-5 w-5" />}</button>
          </form>
        </div>
      )}
      </div>
     </div>
    </div>
  );
}
